import type {
  CcNotificationPreferences,
  RecipientNotificationPreferences,
  SenderNotificationPreferences,
  TemplateNotificationPreferences,
  UserNotificationPreferences,
  UserPreferences,
} from '../../models/auth.models';
import {
  DEFAULT_USER_DATE_TIME_PREFERENCES,
  normalizeUserDateTimePreferences,
  USER_DATE_FORMAT_OPTIONS,
  USER_TIME_FORMAT_OPTIONS,
} from '../../services/user-date-time-format.service';

export interface TimeZoneOption {
  value: string;
  label: string;
  searchText: string;
}

export interface NotificationItemDefinition<TGroup extends keyof UserNotificationPreferences> {
  key: keyof UserNotificationPreferences[TGroup];
  label: string;
}

export interface NotificationGroupDefinition<TGroup extends keyof UserNotificationPreferences> {
  key: TGroup;
  title: string;
  helper: string;
  items: readonly NotificationItemDefinition<TGroup>[];
}

export const DEFAULT_USER_NOTIFICATION_PREFERENCES: UserNotificationPreferences = {
  senderNotifications: {
    recipientViewsDocument: true,
    documentHasBeenSent: false,
    emailDeliveryFailedForRecipient: true,
    receiveDocumentToSignOrApprove: true,
    recipientDeclinesToSign: true,
    documentHasBeenRevoked: true,
    documentCompletedByAllRecipients: true,
    authenticationFailsForDocument: true,
    recipientSignsOrApprovesDocument: true,
    documentHasExpired: true,
    remindersOfDocument: true,
  },
  recipientNotifications: {
    receiveDocumentToSignOrApprove: true,
    recipientDeclinesToSignOrApproveDocument: true,
    senderRevokesDocument: true,
    documentCompletedByAllRecipients: true,
    recipientHasSignedDocument: true,
    documentHasExpired: true,
    senderDeletedDocumentPermanently: true,
  },
  ccNotifications: {
    newSigningRequestSentOut: true,
    recipientDeclinesToSignOrApproveDocument: false,
    senderRevokesDocument: false,
    documentCompletedByAllRecipients: true,
    recipientSignsDocument: false,
    documentHasExpired: false,
    senderDeletedDocumentPermanently: true,
    recipientViewsDocument: false,
  },
  templateNotifications: {
    templateHasBeenShared: true,
    permissionsChangedForSharedTemplate: true,
    templateHasBeenDeleted: true,
  },
};

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  ...DEFAULT_USER_DATE_TIME_PREFERENCES,
  notifications: DEFAULT_USER_NOTIFICATION_PREFERENCES,
};

export const DATE_FORMAT_OPTIONS = USER_DATE_FORMAT_OPTIONS;
export const TIME_FORMAT_OPTIONS = USER_TIME_FORMAT_OPTIONS;

const buildTimeZoneOption = (value: string, label: string): TimeZoneOption => ({
  value,
  label,
  searchText: `${label} ${value}`.toLowerCase(),
});

export const TIME_ZONE_OPTIONS: readonly TimeZoneOption[] = [
  buildTimeZoneOption('UTC', '(UTC+00:00) Coordinated Universal Time (UTC)'),
  buildTimeZoneOption('America/Cayman', '(UTC-05:00) Eastern Standard Time (America/Cayman)'),
  buildTimeZoneOption('America/Chicago', '(UTC-06:00) Central Standard Time (America/Chicago)'),
  buildTimeZoneOption('America/Chihuahua', '(UTC-07:00) Mountain Standard Time (America/Chihuahua)'),
  buildTimeZoneOption('America/Ciudad_Juarez', '(UTC-07:00) Mountain Standard Time (America/Ciudad_Juarez)'),
  buildTimeZoneOption('America/Costa_Rica', '(UTC-06:00) Central Standard Time (America/Costa_Rica)'),
  buildTimeZoneOption('America/Coyhaique', '(UTC-03:00) Chile Summer Time (America/Coyhaique)'),
  buildTimeZoneOption('America/Creston', '(UTC-07:00) Mountain Standard Time (America/Creston)'),
  buildTimeZoneOption('America/Cuiaba', '(UTC-04:00) Amazon Time (America/Cuiaba)'),
  buildTimeZoneOption('America/Curacao', '(UTC-04:00) Atlantic Standard Time (America/Curacao)'),
  buildTimeZoneOption('America/Denver', '(UTC-07:00) Mountain Time (America/Denver)'),
  buildTimeZoneOption('America/Edmonton', '(UTC-07:00) Mountain Time (America/Edmonton)'),
  buildTimeZoneOption('America/Guatemala', '(UTC-06:00) Central Standard Time (America/Guatemala)'),
  buildTimeZoneOption('America/Halifax', '(UTC-04:00) Atlantic Time (America/Halifax)'),
  buildTimeZoneOption('America/Lima', '(UTC-05:00) Peru Time (America/Lima)'),
  buildTimeZoneOption('America/Los_Angeles', '(UTC-08:00) Pacific Time (America/Los_Angeles)'),
  buildTimeZoneOption('America/Mexico_City', '(UTC-06:00) Central Standard Time (America/Mexico_City)'),
  buildTimeZoneOption('America/New_York', '(UTC-05:00) Eastern Time (America/New_York)'),
  buildTimeZoneOption('America/Phoenix', '(UTC-07:00) Mountain Standard Time (America/Phoenix)'),
  buildTimeZoneOption('America/Santiago', '(UTC-04:00) Chile Time (America/Santiago)'),
  buildTimeZoneOption('America/Sao_Paulo', '(UTC-03:00) Brasilia Time (America/Sao_Paulo)'),
  buildTimeZoneOption('America/Toronto', '(UTC-05:00) Eastern Time (America/Toronto)'),
  buildTimeZoneOption('America/Vancouver', '(UTC-08:00) Pacific Time (America/Vancouver)'),
] as const;

export const NOTIFICATION_GROUPS: readonly [
  NotificationGroupDefinition<'senderNotifications'>,
  NotificationGroupDefinition<'recipientNotifications'>,
  NotificationGroupDefinition<'ccNotifications'>,
  NotificationGroupDefinition<'templateNotifications'>,
] = [
  {
    key: 'senderNotifications',
    title: 'Sender Notifications',
    helper: 'As a sender, I want to receive the following notifications.',
    items: [
      { key: 'recipientViewsDocument', label: 'Recipient views the document' },
      { key: 'documentHasBeenSent', label: 'Document has been sent' },
      { key: 'emailDeliveryFailedForRecipient', label: 'Email delivery failed for a recipient' },
      { key: 'receiveDocumentToSignOrApprove', label: 'Receive a document to sign or approve' },
      { key: 'recipientDeclinesToSign', label: 'Recipient declines to sign' },
      { key: 'documentHasBeenRevoked', label: 'Document has been revoked' },
      { key: 'documentCompletedByAllRecipients', label: 'Document has been completed by all recipients' },
      { key: 'authenticationFailsForDocument', label: 'Authentication fails for a document' },
      { key: 'recipientSignsOrApprovesDocument', label: 'Recipient signs or approves a document' },
      { key: 'documentHasExpired', label: 'Document has expired' },
      { key: 'remindersOfDocument', label: 'Reminders of the document' },
    ],
  },
  {
    key: 'recipientNotifications',
    title: 'Recipient Notifications',
    helper: 'As a recipient, I want to receive the following notifications.',
    items: [
      { key: 'receiveDocumentToSignOrApprove', label: 'Receive a document to sign or approve' },
      { key: 'recipientDeclinesToSignOrApproveDocument', label: 'A recipient declines to sign or approve the document' },
      { key: 'senderRevokesDocument', label: 'Sender revokes the document' },
      { key: 'documentCompletedByAllRecipients', label: 'Document has been completed by all recipients' },
      { key: 'recipientHasSignedDocument', label: 'A recipient has signed the document' },
      { key: 'documentHasExpired', label: 'Document has expired' },
      { key: 'senderDeletedDocumentPermanently', label: 'Sender deleted the document permanently' },
    ],
  },
  {
    key: 'ccNotifications',
    title: 'CC Notifications',
    helper: 'As a CC, I want to receive the following notifications.',
    items: [
      { key: 'newSigningRequestSentOut', label: 'New signing request has been sent out' },
      { key: 'recipientDeclinesToSignOrApproveDocument', label: 'Recipient declines to sign or approve the document' },
      { key: 'senderRevokesDocument', label: 'Sender revokes the document' },
      { key: 'documentCompletedByAllRecipients', label: 'Document has been completed by all recipients' },
      { key: 'recipientSignsDocument', label: 'Recipient signs a document' },
      { key: 'documentHasExpired', label: 'Document has expired' },
      { key: 'senderDeletedDocumentPermanently', label: 'Sender deleted the document permanently' },
      { key: 'recipientViewsDocument', label: 'Recipient views the document' },
    ],
  },
  {
    key: 'templateNotifications',
    title: 'Template Notifications',
    helper: 'For shared templates, I want to receive the following notifications.',
    items: [
      { key: 'templateHasBeenShared', label: 'Template has been shared' },
      { key: 'permissionsChangedForSharedTemplate', label: 'Permissions have been changed for an already shared template' },
      { key: 'templateHasBeenDeleted', label: 'Template has been deleted' },
    ],
  },
] as const;

const normalizeBooleanRecord = <T extends { [K in keyof T]: boolean }>(defaults: T, input: unknown): T => {
  const record = input && typeof input === 'object' ? (input as Record<string, unknown>) : {};
  const normalized = { ...defaults } as T;

  for (const key of Object.keys(defaults) as Array<keyof T>) {
    normalized[key] =
      typeof record[key as string] === 'boolean'
        ? (record[key as string] as T[keyof T])
        : defaults[key];
  }

  return normalized;
};

const normalizeUserNotificationPreferences = (input?: Partial<UserNotificationPreferences> | null): UserNotificationPreferences => {
  return {
    senderNotifications: normalizeBooleanRecord(DEFAULT_USER_NOTIFICATION_PREFERENCES.senderNotifications, input?.senderNotifications),
    recipientNotifications: normalizeBooleanRecord(
      DEFAULT_USER_NOTIFICATION_PREFERENCES.recipientNotifications,
      input?.recipientNotifications,
    ),
    ccNotifications: normalizeBooleanRecord(DEFAULT_USER_NOTIFICATION_PREFERENCES.ccNotifications, input?.ccNotifications),
    templateNotifications: normalizeBooleanRecord(
      DEFAULT_USER_NOTIFICATION_PREFERENCES.templateNotifications,
      input?.templateNotifications,
    ),
  };
};

export const normalizeUserPreferences = (input?: Partial<UserPreferences> | null): UserPreferences => {
  const normalizedDateTime = normalizeUserDateTimePreferences(input);
  const timeZone = TIME_ZONE_OPTIONS.some((option) => option.value === normalizedDateTime.timeZone)
    ? normalizedDateTime.timeZone
    : DEFAULT_USER_PREFERENCES.timeZone;

  return {
    dateFormat: normalizedDateTime.dateFormat,
    timeFormat: normalizedDateTime.timeFormat,
    timeZone,
    notifications: normalizeUserNotificationPreferences(input?.notifications),
  };
};

export const cloneNotificationPreferences = (notifications: UserNotificationPreferences): UserNotificationPreferences => ({
  senderNotifications: { ...notifications.senderNotifications },
  recipientNotifications: { ...notifications.recipientNotifications },
  ccNotifications: { ...notifications.ccNotifications },
  templateNotifications: { ...notifications.templateNotifications },
});

export const countEnabledNotifications = <T extends object>(group: T): number => {
  return Object.values(group as Record<string, boolean>).filter(Boolean).length;
};

export const countTotalNotifications = <T extends object>(group: T): number => {
  return Object.keys(group as Record<string, boolean>).length;
};
